/**
 * SyncCheck — vergelijkt Airtable projecten met Gripp en toont mismatches.
 *
 * Read-only V1: laat zien wat uit-sync is. Gebruiker fixt handmatig in
 * Airtable of Gripp. Direct-link naar Airtable record per row.
 *
 * Joris-only via email-allowlist.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Link,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { Navigate } from "react-router-dom";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";

import { authFetch } from "../../utils/authHelpers";
import { AI_ENGINE_URL } from "../../utils/apiConfig";

const ALLOWED_EMAILS = new Set(["joris@fullframe.nl"]);

// Airtable base/table IDs voor Projecten — om direct-link te bouwen
// (komen overeen met backend/utils/airtableConfig.js)
const AIRTABLE_BASE = "apphMT5j6gtUnMY1p";
const AIRTABLE_PROJECTS_TABLE = "tblUVT2dDpafP8rjq";

function airtableLink(recordId) {
  if (!recordId) return null;
  return `https://airtable.com/${AIRTABLE_BASE}/${AIRTABLE_PROJECTS_TABLE}/${recordId}`;
}

function fmtEuro(n) {
  if (n == null) return "—";
  if (n >= 1000000) return `€${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `€${(n / 1000).toFixed(1)}k`;
  return `€${n.toFixed(0)}`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SyncCheck() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const userEmail = (account?.username || "").toLowerCase();

  if (!ALLOWED_EMAILS.has(userEmail)) {
    return <Navigate to="/" replace />;
  }

  return <SyncCheckBody />;
}

function SyncCheckBody() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onlyMismatches, setOnlyMismatches] = useState(true);
  const [onlyActive, setOnlyActive] = useState(true);
  const [issueFilter, setIssueFilter] = useState("alle"); // "alle" | "status" | "value" | "missing_in_gripp" | "no_gripp_link"
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState({}); // projectNumber → boolean
  const [syncedFlash, setSyncedFlash] = useState({}); // projectNumber → "success"|"error"

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set("only_mismatches", onlyMismatches ? "true" : "false");
        params.set("only_active", onlyActive ? "true" : "false");
        const res = await authFetch(`${AI_ENGINE_URL}/api/v1/sync-check/sync-check?${params}`);
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Kon sync-check niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [onlyMismatches, onlyActive, refreshKey]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    let out = data.rows;
    if (issueFilter !== "alle") {
      out = out.filter((r) => (r.issues || []).includes(issueFilter));
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter((r) =>
        String(r.projectNumber || "").includes(s) ||
        (r.name || "").toLowerCase().includes(s) ||
        (r.klant || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [data, issueFilter, search]);

  async function handleSync(projectNumber) {
    if (!projectNumber) return;
    setSyncing((s) => ({ ...s, [projectNumber]: true }));
    try {
      const res = await authFetch(
        `${AI_ENGINE_URL}/api/v1/gripp/sync/project/${projectNumber}`,
        { method: "POST" }
      );
      const ok = res.ok;
      setSyncedFlash((s) => ({ ...s, [projectNumber]: ok ? "success" : "error" }));
    } catch {
      setSyncedFlash((s) => ({ ...s, [projectNumber]: "error" }));
    } finally {
      setSyncing((s) => ({ ...s, [projectNumber]: false }));
      setTimeout(() => {
        setSyncedFlash((s) => {
          const { [projectNumber]: _, ...rest } = s;
          return rest;
        });
      }, 4000);
    }
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
      <PageHeader onRefresh={() => setRefreshKey((k) => k + 1)} loading={loading} />

      <SummaryCard data={data} loading={loading} />

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, border: "1px solid #e5e7eb" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <FormControlLabel
            control={<Switch checked={onlyMismatches} onChange={(e) => setOnlyMismatches(e.target.checked)} />}
            label="Alleen mismatches"
          />
          <FormControlLabel
            control={<Switch checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} />}
            label="Alleen actieve"
          />
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FilterListIcon sx={{ color: "text.secondary", fontSize: 18 }} />
            {["alle", "status", "value", "missing_in_gripp", "no_gripp_link"].map((opt) => (
              <Chip
                key={opt}
                label={
                  opt === "alle" ? "Alle issues" :
                  opt === "status" ? "Status mismatch" :
                  opt === "value" ? "Waarde mismatch" :
                  opt === "missing_in_gripp" ? "Niet in Gripp" :
                  "Geen Gripp link"
                }
                size="small"
                clickable
                variant={issueFilter === opt ? "filled" : "outlined"}
                onClick={() => setIssueFilter(opt)}
                sx={{
                  bgcolor: issueFilter === opt ? "#fef3c7" : "transparent",
                  color: issueFilter === opt ? "#92400e" : "text.secondary",
                  fontWeight: issueFilter === opt ? 600 : 400,
                }}
              />
            ))}
          </Stack>
          <TextField
            size="small"
            placeholder="Zoek op nr / naam / klant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 3, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <Table
            rows={filteredRows}
            onSync={handleSync}
            syncing={syncing}
            syncedFlash={syncedFlash}
          />
        </Paper>
      )}
    </Box>
  );
}

// ── Header ───────────────────────────────────────────────────────────────────

function PageHeader({ onRefresh, loading }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: 2.5,
        bgcolor: "#fef3c7", color: "#92400e",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <SyncProblemIcon />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.025em" }}>
          Sync-check Airtable ↔ Gripp
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welke projecten lopen uit-sync? Status, opdrachtwaarde, ontbrekende data.
        </Typography>
      </Box>
      <Tooltip title="Vernieuwen">
        <IconButton onClick={onRefresh} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>
      <Chip label="Privé" size="small" sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600 }} />
    </Box>
  );
}

// ── Summary ─────────────────────────────────────────────────────────────────

function SummaryCard({ data, loading }) {
  if (loading || !data) return null;
  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
      gap: 2, mb: 2,
    }}>
      <SummaryStat label="Gecheckt" value={data.checked} color="#6366f1" />
      <SummaryStat label="Mismatches" value={data.mismatches} color={data.mismatches > 0 ? "#dc2626" : "#10b981"} />
      <SummaryStat label="Niet in Gripp" value={data.missingInGripp} color="#f59e0b" />
      <SummaryStat label="Zonder Gripp link" value={data.noGrippLink} color="#94a3b8" />
    </Box>
  );
}

function SummaryStat({ label, value, color }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2.5, border: "1px solid #e5e7eb" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color, lineHeight: 1.1, mt: 0.5 }}>
        {value}
      </Typography>
    </Paper>
  );
}

// ── Table ────────────────────────────────────────────────────────────────────

function Table({ rows, onSync, syncing, syncedFlash }) {
  if (rows.length === 0) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CheckIcon sx={{ fontSize: 48, color: "#10b981", mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Geen mismatches gevonden</Typography>
        <Typography variant="caption" color="text.secondary">
          Alles in sync — of pas filters aan om meer te zien.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: "70px 2fr 1.4fr 1.2fr 1.2fr 1fr 1fr 80px 90px",
        gap: 1, alignItems: "center",
        px: 2, py: 1.5, fontSize: 11, fontWeight: 600, color: "text.secondary",
        bgcolor: "#fafafa", borderBottom: "1px solid #e5e7eb",
        minWidth: 1100,
      }}>
        <Box>Nr</Box>
        <Box>Project</Box>
        <Box>Klant</Box>
        <Box>Airtable status</Box>
        <Box>Verwacht (Gripp)</Box>
        <Box sx={{ textAlign: "right" }}>Airtable €</Box>
        <Box sx={{ textAlign: "right" }}>Gripp €</Box>
        <Box>Issues</Box>
        <Box />
      </Box>
      {rows.map((r) => (
        <Row
          key={r.airtableId || r.projectNumber}
          row={r}
          onSync={onSync}
          syncing={syncing[r.syncCommand]}
          flash={syncedFlash[r.syncCommand]}
        />
      ))}
    </Box>
  );
}

function Row({ row, onSync, syncing, flash }) {
  const link = airtableLink(row.airtableId);

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "70px 2fr 1.4fr 1.2fr 1.2fr 1fr 1fr 80px 90px",
      gap: 1, alignItems: "center",
      px: 2, py: 1.25, borderBottom: "1px solid #f1f5f9",
      "&:hover": { bgcolor: "#fafafa" },
      minWidth: 1100,
    }}>
      <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#6366f1", fontWeight: 600 }}>
        {row.projectNumber || "—"}
      </Typography>

      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }} noWrap>
          {row.name || "(geen naam)"}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" noWrap>
        {row.klant || "—"}
      </Typography>

      <StatusCell value={row.airtableStatus} match={row.statusMatch} />

      <StatusCell value={row.expectedStatus} expected />

      <Typography variant="caption" sx={{ textAlign: "right", fontFamily: "monospace" }}>
        {fmtEuro(row.airtableValue)}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          textAlign: "right", fontFamily: "monospace",
          color: row.valueMatch ? "text.secondary" : "#dc2626",
          fontWeight: row.valueMatch ? 400 : 600,
        }}
      >
        {fmtEuro(row.grippValue)}
        {row.valueDiff != null && Math.abs(row.valueDiff) > 1 && (
          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: "#dc2626", fontSize: 10 }}>
            (Δ {fmtEuro(Math.abs(row.valueDiff))})
          </Typography>
        )}
      </Typography>

      <Stack direction="row" spacing={0.25} flexWrap="wrap">
        {(row.issues || []).map((iss) => (
          <Chip
            key={iss}
            label={
              iss === "status" ? "Status" :
              iss === "value" ? "€" :
              iss === "missing_in_gripp" ? "Geen Gripp" :
              iss === "no_gripp_link" ? "Geen link" : iss
            }
            size="small"
            sx={{
              height: 18, fontSize: 9, fontWeight: 600,
              bgcolor: "#fee2e2", color: "#b91c1c",
            }}
          />
        ))}
      </Stack>

      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
        {row.syncCommand && (
          <Tooltip title={
            flash === "success" ? "Gripp data ververst ✓ — refresh nodig om Airtable mismatch te zien" :
            flash === "error" ? "Sync mislukt" :
            "Trek Gripp data opnieuw op (update PG cache; Airtable moet je apart fixen)"
          } arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => onSync(row.syncCommand)}
                disabled={syncing}
                sx={{
                  color: flash === "success" ? "#10b981" : flash === "error" ? "#dc2626" : "#6366f1",
                  bgcolor: flash === "success" ? "#dcfce7" : flash === "error" ? "#fee2e2" : "transparent",
                }}
              >
                {syncing
                  ? <CircularProgress size={16} />
                  : flash === "success"
                    ? <CheckIcon fontSize="small" />
                    : flash === "error"
                      ? <CloseIcon fontSize="small" />
                      : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {link && (
          <Tooltip title="Open in Airtable" arrow>
            <IconButton size="small" component={Link} href={link} target="_blank" rel="noopener noreferrer" sx={{ color: "#6366f1" }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

function StatusCell({ value, match, expected }) {
  if (!value) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  let color = "#475569";
  let bg = "#f1f5f9";
  if (match === true) { color = "#166534"; bg = "#dcfce7"; }
  if (match === false) { color = "#b91c1c"; bg = "#fee2e2"; }
  if (expected) { color = "#1e40af"; bg = "#dbeafe"; }

  return (
    <Chip
      label={value}
      size="small"
      sx={{ height: 22, fontSize: 11, bgcolor: bg, color, fontWeight: 600 }}
    />
  );
}
